import * as Sentry from "@sentry/browser";
import {getAuth} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";
import {useObjectVal} from "react-firebase-hooks/database";
import {ref} from "firebase/database";
import {database, functions} from "hooks/firebase";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {useRankedBrandTraits} from "data/access/survey-question-pages/brand-traits";
import {joinStringsWithCommaAnd} from "helpers/join-strings-with-and";
import {ChoiceValue} from "data/models/survey/choice-base";
import {useWordScores} from "data/access/survey-question-pages/word-scores";
import {useKeyMessages} from "data/access/survey-question-pages/key-messages";
import {httpsCallable} from "firebase/functions";
import {OpenAiApiRequest, OpenAiApiResponse} from "data/models/shared/open-ai-api";
import {assertNoError} from "helpers/asserts";
import {useCallback, useMemo, useState} from "react";

export type BrandVoiceResultType = {
  prompt: string;
  voice: string;
  maxGuidelinesLength: number;
}

const NO_BRAND_VOICE = {
  prompt: '',
  voice: '',
  maxGuidelinesLength: 500,
};

export const useGenerateBrandVoice = (): {
  generateBrandVoice: () => Promise<BrandVoiceResultType>,
  generatePrompt: (maximumGuidelinesLength: number) => string | null,
  readyToGenerateBrandVoice: boolean,
} => {
  const auth = getAuth();
  const [user] = useAuthState(auth);
  const canvaAssetsPath = useMemo(() => `user-data/${user?.uid ?? ""}/user-surveys/canva-assets/all-features`, [user]);
  const [businessName, businessNameLoading, businessNameError] =
      useObjectVal<string>((user && ref(database, `${canvaAssetsPath}/business-info/business-name`)) ?? null);
  const [businessDescription, businessDescriptionLoading, businessDescriptionError] =
      useObjectVal<string>((user && ref(database, `${canvaAssetsPath}/business-info/business-description`)) ?? null);
  const [rankedClientAttributes, rankedClientAttributesLoading, rankedClientAttributesError] =
      useObjectVal<ChoiceValue[]>((user && ref(database, `${canvaAssetsPath}/your-customers/ranked-client-attributes`)) ?? null);
  const [differentiatorsRanked, differentiatorsRankedLoading, differentiatorsRankedError] =
      useObjectVal<ChoiceValue[]>((user && ref(database, `${canvaAssetsPath}/what-makes-you-special/differentiators-ranked`)) ?? null);
  const [rankedBrandTraits, rankedTraitsLoading, rankedTraitsError] = useRankedBrandTraits();
  const [wordScores, wordScoresLoading, wordScoresError] = useWordScores();
  const [keyMessages, keyMessagesLoading, keyMessagesError] = useKeyMessages();


  const isLoading = (
      businessNameLoading
      || businessDescriptionLoading
      || rankedTraitsLoading
      || rankedClientAttributesLoading
      || differentiatorsRankedLoading
      || wordScoresLoading
      || keyMessagesLoading
  );

  const missesAnyData = (
      isNullOrUndefined(businessName)
      || isNullOrUndefined(businessDescription)
      || isNullOrUndefined(rankedBrandTraits)
      || isNullOrUndefined(rankedClientAttributes)
      || isNullOrUndefined(differentiatorsRanked)
      || isNullOrUndefined(wordScores)
      || isNullOrUndefined(keyMessages)
  )

  const readyToGenerateBrandVoice = !isLoading && !missesAnyData;

  assertNoError(
      businessNameError
      ?? businessDescriptionError
      ?? rankedTraitsError
      ?? rankedClientAttributesError
      ?? differentiatorsRankedError
      ?? wordScoresError
      ?? keyMessagesError
  );

  const generatePrompt = useCallback((maximumGuidelinesLength: number): string | null => {
    if (!readyToGenerateBrandVoice) {
      return null;
    }
    const greenScores = wordScores.filter(wordScore => wordScore.score > 0);
    const redScores = wordScores.filter(wordScore => wordScore.score < 0);

    const businessDescriptionStartingArticle = (() => {
      if (isNullOrUndefined(businessDescription) || businessDescription.length === 0) {
        return "";
      }
      if (businessDescription.substring(0, 2).toLowerCase() === "a " || businessDescription.substring(0, 3).toLowerCase() === "an " || businessDescription.substring(0, 4).toLowerCase() === "the ") {
        return "";
      } else if (["a", "e", "i", "o", "u"].includes(businessDescription.charAt(0).toLowerCase())) {
        return "an ";
      } else {
        return "a ";
      }
    })()

    return `I am a brand manager who wants to ensure that ${businessName} always presents itself using on-brand words and tone of voice. I am developing guidelines that:
- are clear and easy to follow
- state the brand traits
- make the brand appealing to their ideal clients
- Emphasize how ${businessName} is different and special
- Provides do's and don'ts

${businessName} is ${businessDescriptionStartingArticle}${businessDescription}.

${rankedBrandTraits.length !== 0 ? `The ${businessName}'s top brand traits are ${joinStringsWithCommaAnd(rankedBrandTraits.slice(0, 3).map(t => t.traitKey))}.\n` : ''} 
${rankedClientAttributes.length !== 0 ? `Their ideal clients are: ${joinStringsWithCommaAnd(rankedClientAttributes.slice(0, 3).map(a => a.value))}.\n` : ''}
${differentiatorsRanked.length !== 0 ? `Compared to its competitors, ${businessName} is ${joinStringsWithCommaAnd(differentiatorsRanked.slice(0, 3).map(a => a.value))}.\n` : ''}
${greenScores.length !== 0 ? `Our brand voice should emphasize these words or ideas: ${joinStringsWithCommaAnd(greenScores.slice(0, 3).map(a => a.value))}.` : ''}${redScores.length !== 0 ? ` The brand voice should avoid words and ideas like: ${joinStringsWithCommaAnd(redScores.slice(0, 3).map(a => a.value))}.` : ''}

${keyMessages.slice(0, 3).map(keyMessage => `Our message should be: ${keyMessage.name} ${keyMessage.description.map(({lines}) => lines.join(" ")).join(" ")}`).join("\n\n").replace("{business-name}", businessName)}

Create brand voice guidelines that will be used in Canva's brand kit for ${businessName}. Do not use markdown. The allowed maximum length is ${maximumGuidelinesLength} characters including whitespace.
Start with the phrase, "${businessName}'s tone of voice is: ${rankedBrandTraits.length !== 0 ? `${joinStringsWithCommaAnd(rankedBrandTraits.slice(0, 3).map(t => t.traitKey))}.` : ''}"
`;
  }, [
    readyToGenerateBrandVoice,
    wordScores,
    businessDescription,
    businessName,
    rankedBrandTraits,
    rankedClientAttributes,
    differentiatorsRanked,
    keyMessages,
  ]);

  const generateBrandVoice = async (): Promise<BrandVoiceResultType> => {
    if (!readyToGenerateBrandVoice) {
      return Promise.resolve(NO_BRAND_VOICE);
    }

    let currentMaximumGuidelinesLength = 440;
    let attemptsLeft = 4;
    let previousVoice: string | null = null;
    let previousPrompt: string | null = null;
    let previousMaximumGuidelinesLength: number | null = null;

    while (attemptsLeft > 0) {
      const prompt = generatePrompt(currentMaximumGuidelinesLength);

      if (prompt === null) {
        throw new Error("Unexpectedly prompt is null.")
      }

      const openAiApiFunction = httpsCallable<OpenAiApiRequest, OpenAiApiResponse>(functions, "openAiApi");

      const {data: {response: voice}} = await openAiApiFunction({prompt});

      if (previousVoice === null) {
        previousVoice = voice;
        previousPrompt = prompt;
        previousMaximumGuidelinesLength = currentMaximumGuidelinesLength;
      }

      if (voice.length > 500) {
        currentMaximumGuidelinesLength -= 20;
        attemptsLeft--;
        continue;
      }

      if (previousVoice.length > 500 || voice.length > previousVoice.length) {
        previousVoice = voice;
        previousPrompt = prompt;
        previousMaximumGuidelinesLength = currentMaximumGuidelinesLength;
      }

      attemptsLeft--;
      if (attemptsLeft === 0 || previousVoice.length > 490) {
        break;
      }
      currentMaximumGuidelinesLength += 10;
    }
    if (previousPrompt === null || previousVoice === null || previousMaximumGuidelinesLength === null) {
      throw new Error("Could not generate prompt under 500 characters.")
    }
    if (previousVoice.length > 500) {
      Sentry.captureException(new Error("Generated voice is longer than 500 characters."))
    }
    return {
      prompt: previousPrompt,
      voice: previousVoice,
      maxGuidelinesLength: previousMaximumGuidelinesLength,
    };
  }

  return {generateBrandVoice, generatePrompt, readyToGenerateBrandVoice};
}
